import React, { useRef, useState, useEffect } from "react";
import { Map, TileLayer, Marker, Popup, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/RouteMap.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function getStopColor(index, total) {
  var ratio = total > 1 ? index / (total - 1) : 0;
  var hue = Math.round(20 + ratio * 220);
  return "hsl(" + hue + ", 80%, 45%)";
}

function getBearing(lat1, lng1, lat2, lng2) {
  var dLon = (lng2 - lng1) * Math.PI / 180;
  var la1 = lat1 * Math.PI / 180;
  var la2 = lat2 * Math.PI / 180;
  var y = Math.sin(dLon) * Math.cos(la2);
  var x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

var STATUS_LABEL = {
  pending: "Pendiente",
  in_route: "En ruta",
  at_destination: "En destino",
  delivered: "Entregado",
  completed: "Entregado",
  failed: "Fallido",
};

var STATUS_COLOR = {
  pending: "#94a3b8",
  in_route: "#2563eb",
  at_destination: "#ea580c",
  delivered: "#16a34a",
  completed: "#16a34a",
  failed: "#dc2626",
};

function fmtPurchaseTime(dt) {
  if (!dt) return null;
  try {
    const d = new Date(dt);
    const date = d.toLocaleDateString("es-PE", { day: "2-digit", month: "2-digit", timeZone: "America/Lima" });
    const time = d.toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/Lima" });
    return `${date} ${time}`;
  } catch { return null; }
}

const RouteMap = ({ route }) => {
  const mapRef = useRef(null);
  const markerRefs = useRef({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapActive, setMapActive] = useState(false);
  const [selectedStopId, setSelectedStopId] = useState(null);

  var isTouch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  useEffect(function() {
    if (!isFullscreen) setMapActive(false);
    var timer = setTimeout(function() {
      if (mapRef.current && mapRef.current.leafletElement) {
        mapRef.current.leafletElement.invalidateSize();
      }
    }, 300);
    return function() { clearTimeout(timer); };
  }, [isFullscreen]);

  useEffect(function() {
    var handleEsc = function(e) { if (e.key === "Escape") setIsFullscreen(false); };
    window.addEventListener("keydown", handleEsc);
    return function() { window.removeEventListener("keydown", handleEsc); };
  }, []);

  var handleStopSelect = function(stop) {
    setSelectedStopId(stop.id);
    setMapActive(true);
    var lat = parseFloat(stop.coordinates_latitude);
    var lng = parseFloat(stop.coordinates_longitude);
    if (mapRef.current && mapRef.current.leafletElement) {
      mapRef.current.leafletElement.flyTo([lat, lng], 16, { animate: true, duration: 0.7 });
    }
  };

  var handleMapClick = function() {
    if (!mapActive && !isFullscreen) setMapActive(true);
  };

  var handleMapBlur = function() {
    if (mapActive && !isFullscreen && !isTouch) setMapActive(false);
  };

  var toggleFullscreen = function() { setIsFullscreen(function(f) { return !f; }); };

  if (!route || !route.stops || route.stops.length === 0) {
    return (
      <div className="route-map-outer">
        <div className="route-map-container">
          <div className="no-route-message">
            <p>📍 No hay ruta para mostrar</p>
            <p className="text-muted">Selecciona una ruta para ver el mapa</p>
          </div>
        </div>
      </div>
    );
  }

  var validStops = route.stops
    .filter(function(s) {
      return s.coordinates_latitude !== null && s.coordinates_longitude !== null &&
        !isNaN(parseFloat(s.coordinates_latitude)) && !isNaN(parseFloat(s.coordinates_longitude));
    })
    .sort(function(a, b) { return a.stop_number - b.stop_number; });

  if (validStops.length === 0) {
    return (
      <div className="route-map-outer">
        <div className="route-map-container">
          <div className="no-route-message">
            <p>⚠️ Las paradas no tienen coordenadas GPS</p>
          </div>
        </div>
      </div>
    );
  }

  var routeCoordinates = validStops.map(function(s) {
    return [parseFloat(s.coordinates_latitude), parseFloat(s.coordinates_longitude)];
  });

  var deliveryStops = validStops.filter(function(s) { return s.stop_type === "DELIVERY"; });
  var totalDeliveries = deliveryStops.length;

  // Flechas de dirección en el punto medio de cada segmento
  var arrowMarkers = [];
  for (var i = 0; i < routeCoordinates.length - 1; i++) {
    var lat1 = routeCoordinates[i][0], lng1 = routeCoordinates[i][1];
    var lat2 = routeCoordinates[i + 1][0], lng2 = routeCoordinates[i + 1][1];
    arrowMarkers.push({
      lat: (lat1 + lat2) / 2,
      lng: (lng1 + lng2) / 2,
      bearing: getBearing(lat1, lng1, lat2, lng2),
    });
  }

  var createDeliveryIcon = function(stopNumber, deliveryIndex, isSelected) {
    var color = getStopColor(deliveryIndex, totalDeliveries);
    var size = isSelected ? 44 : 36;
    var fontSize = isSelected ? "17px" : "14px";
    var ring = isSelected ? "ring: 0 0 0 3px white, 0 0 0 5px " + color + ";" : "";
    return L.divIcon({
      className: "stop-delivery-icon",
      html: '<div class="stop-circle' + (isSelected ? " stop-circle--selected" : "") + '" style="width:' + size + 'px;height:' + size + 'px;background:' + color + ';font-size:' + fontSize + '">' + stopNumber + '</div>',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -(size / 2 + 4)],
    });
  };

  var startIcon = L.divIcon({
    className: "stop-special-icon",
    html: '<div class="stop-special stop-special--start">🏠</div>',
    iconSize: [44, 44], iconAnchor: [22, 22], popupAnchor: [0, -26],
  });

  var returnIcon = L.divIcon({
    className: "stop-special-icon",
    html: '<div class="stop-special stop-special--return">🔁</div>',
    iconSize: [40, 40], iconAnchor: [20, 20], popupAnchor: [0, -24],
  });

  var routeColor = validStops.some(function(s) { return s.using_real_traffic; }) ? "#2563eb" : "#94a3b8";

  return (
    <div className={"route-map-outer" + (isFullscreen ? " route-map-outer--fullscreen" : "")}>
      <div className="route-map-container">

        {/* Header */}
        <div className="map-header">
          <div className="map-header-left">
            <h3>🗺️ {route.name}</h3>
            <div className="map-stats">
              <span className="map-stat">📍 {totalDeliveries} entregas</span>
              <span className="map-stat">🛣️ {Number(route.total_distance || 0).toFixed(1)} km</span>
              <span className="map-stat">⏱️ {route.estimated_duration || 0} min</span>
              {validStops.some(function(s) { return s.using_real_traffic; }) && (
                <span className="map-stat traffic-real">🚦 Tráfico real</span>
              )}
            </div>
          </div>
          <button className="btn-fullscreen" onClick={toggleFullscreen}
            title={isFullscreen ? "Salir (ESC)" : "Pantalla completa"}>
            {isFullscreen ? "✕ Cerrar" : "⛶ Ampliar"}
          </button>
        </div>

        {/* Cuerpo: panel de paradas + mapa */}
        <div className="map-body">

          {/* Panel lateral de paradas — incluye Inicio, entregas y Retorno */}
          <div className="stop-list-panel">
            <div className="stop-list-header">
              <span>Paradas</span>
              <span className="stop-list-count">{validStops.length}</span>
            </div>
            <div className="stop-list-scroll">
              {validStops.map(function(stop) {
                var isStart = stop.stop_type === "DEPARTURE";
                var isRet = stop.stop_type === "RETURN";
                var deliveryIndex = deliveryStops.findIndex(function(d) { return d.id === stop.id; });
                var color = isStart ? "#16a34a"
                  : isRet ? "#64748b"
                  : getStopColor(deliveryIndex, totalDeliveries);
                var statusKey = stop.delivery_status || stop.status || "pending";
                var statusColor = STATUS_COLOR[statusKey] || "#94a3b8";
                var statusLabel = STATUS_LABEL[statusKey] || statusKey;
                var isSelected = selectedStopId === stop.id;
                var label = isStart ? "🏠" : isRet ? "🔁" : stop.stop_number;
                var nameLabel = isStart ? "Inicio / Depósito"
                  : isRet ? "Retorno al depósito"
                  : (stop.customer_name || "Sin nombre");
                return (
                  <div
                    key={stop.id}
                    className={"stop-list-item" + (isSelected ? " stop-list-item--selected" : "") + (isStart || isRet ? " stop-list-item--special" : "")}
                    onClick={function() { handleStopSelect(stop); }}
                  >
                    <div className="stop-list-num" style={{ background: color }}>
                      {label}
                    </div>
                    <div className="stop-list-info">
                      <div className="stop-list-name">{nameLabel}</div>
                      {!isStart && !isRet && (
                        <div className="stop-list-address">{stop.address_full || "Sin dirección"}</div>
                      )}
                      <div className="stop-list-meta">
                        {stop.time_window_start && (
                          <span className="stop-list-time">
                            ⏰ {stop.time_window_start}–{stop.time_window_end}
                          </span>
                        )}
                        {stop.distance_from_previous > 0 && (
                          <span className="stop-list-dist">
                            {Number(stop.distance_from_previous).toFixed(1)} km
                          </span>
                        )}
                        {!isStart && !isRet && fmtPurchaseTime(stop.purchase_created_at) && (
                          <span className="stop-list-dist">
                            🛒 {fmtPurchaseTime(stop.purchase_created_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    {!isStart && !isRet && (
                      <div className="stop-list-status">
                        <div className="stop-status-dot" style={{ background: statusColor }} title={statusLabel} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Área del mapa */}
          <div className="map-area">
            {!isFullscreen && !mapActive && (
              <div className="map-nav-hint" onClick={handleMapClick}>
                <div className="map-nav-hint-box">
                  <span className="map-nav-hint-icon">{isTouch ? "👆" : "🖱️"}</span>
                  <span>{isTouch ? "Toca para navegar" : "Clic para activar"}</span>
                </div>
              </div>
            )}

            <Map
              bounds={routeCoordinates}
              boundsOptions={{ padding: [40, 40] }}
              style={{ height: "100%", width: "100%" }}
              className="route-leaflet-map"
              ref={mapRef}
              scrollWheelZoom={isFullscreen || mapActive}
              dragging={isFullscreen || mapActive}
              onClick={handleMapClick}
              onMouseOut={handleMapBlur}
            >
              <TileLayer
                attribution="&copy; OpenStreetMap"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <Polyline positions={routeCoordinates} color={routeColor} weight={4} opacity={0.65}>
                <Tooltip sticky>
                  <strong>{route.name}</strong><br />
                  {Number(route.total_distance || 0).toFixed(2)} km · {route.estimated_duration || 0} min
                </Tooltip>
              </Polyline>

              {/* Flechas de dirección */}
              {arrowMarkers.map(function(arrow, idx) {
                return (
                  <Marker
                    key={"arrow-" + idx}
                    position={[arrow.lat, arrow.lng]}
                    icon={L.divIcon({
                      className: "route-arrow-icon-wrapper",
                      html: '<div class="route-arrow-icon" style="transform:rotate(' + arrow.bearing + 'deg)">▶</div>',
                      iconSize: [14, 14], iconAnchor: [7, 7],
                    })}
                    interactive={false}
                    zIndexOffset={-1000}
                  />
                );
              })}

              {/* Paradas */}
              {validStops.map(function(stop) {
                var position = [parseFloat(stop.coordinates_latitude), parseFloat(stop.coordinates_longitude)];
                var isStart = stop.stop_type === "DEPARTURE";
                var isRet = stop.stop_type === "RETURN";
                var deliveryIndex = deliveryStops.findIndex(function(d) { return d.id === stop.id; });
                var isSelected = selectedStopId === stop.id;

                var icon = isStart ? startIcon
                  : isRet ? returnIcon
                  : createDeliveryIcon(stop.stop_number, deliveryIndex, isSelected);

                return (
                  <Marker
                    key={stop.id}
                    position={position}
                    icon={icon}
                    zIndexOffset={isSelected ? 1000 : isStart ? 500 : 0}
                    ref={function(el) { if (el) markerRefs.current[stop.id] = el; }}
                    onClick={function() { setSelectedStopId(stop.id); }}
                  >
                    <Popup maxWidth={300}>
                      <div className="stop-popup">
                        <div className="popup-header">
                          <strong>
                            {isStart ? "🏠 Inicio" : isRet ? "🔁 Retorno" : "Parada " + stop.stop_number}
                          </strong>
                        </div>
                        <div className="popup-content">
                          <p><strong>Cliente:</strong> {stop.customer_name || "Sin nombre"}</p>
                          <p><strong>Dirección:</strong> {stop.address_full || "Sin dirección"}</p>
                          {stop.stop_type === "DELIVERY" && (
                            <React.Fragment>
                              <hr />
                              <p>
                                {stop.order_type === "FIJA"
                                  ? <span className="popup-badge popup-badge-fija">🏠 FIJA</span>
                                  : <span className="popup-badge popup-badge-dinamica">🔄 DINÁMICA</span>}
                              </p>
                              {stop.leave_your_order && (
                                <p><strong>Instrucciones:</strong> {stop.leave_your_order}</p>
                              )}
                              <p><strong>Distancia:</strong> {Number(stop.distance_from_previous || 0).toFixed(2)} km</p>
                              <p><strong>Tiempo:</strong> {stop.travel_time || 0} min</p>
                              {stop.time_window_start && (
                                <p>⏰ {stop.time_window_start} - {stop.time_window_end}</p>
                              )}
                              {stop.original_window_start && stop.original_window_start !== stop.time_window_start && (
                                <p className="popup-moved">
                                  ⏩ Original: {stop.original_window_start} - {stop.original_window_end}
                                </p>
                              )}
                              {fmtPurchaseTime(stop.purchase_created_at) && (
                                <p>🛒 <strong>Pedido hecho:</strong> {fmtPurchaseTime(stop.purchase_created_at)}</p>
                              )}
                            </React.Fragment>
                          )}
                        </div>
                        <div className="popup-actions">
                          <button className="btn-popup"
                            onClick={function() {
                              window.open(
                                "https://www.google.com/maps/search/?api=1&query=" +
                                  stop.coordinates_latitude + "," + stop.coordinates_longitude,
                                "_blank"
                              );
                            }}>
                            🗺️ Google Maps
                          </button>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </Map>
          </div>
        </div>

        {/* Leyenda */}
        <div className="map-legend">
          <div className="legend-gradient-row">
            <span className="legend-gradient-label">Orden:</span>
            <div className="legend-gradient-bar">
              <span className="legend-gradient-dot" style={{ background: getStopColor(0, totalDeliveries) }}>1</span>
              <div className="legend-gradient-track" />
              <span className="legend-gradient-dot" style={{ background: getStopColor(totalDeliveries - 1, totalDeliveries) }}>{totalDeliveries}</span>
            </div>
          </div>
          <div className="legend-items">
            <div className="legend-item"><span>🏠</span><span>Inicio</span></div>
            <div className="legend-item"><span>🔁</span><span>Retorno</span></div>
            <div className="legend-item"><span className="legend-arrow">▶</span><span>Dirección</span></div>
          </div>
          <div className="legend-status-row">
            {Object.keys(STATUS_COLOR).filter(function(k) { return k !== "completed"; }).map(function(k) {
              return (
                <div key={k} className="legend-item">
                  <div className="legend-status-dot" style={{ background: STATUS_COLOR[k] }} />
                  <span>{STATUS_LABEL[k]}</span>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default RouteMap;
